import { CONFIG } from '../config';
import type { CloudinaryAsset } from './types';

/**
 * Variantes que se descargan al tótem. Cada una está dimensionada al píxel para la pantalla
 * 1080×1920 (escala 1): nada se decodifica más grande de lo que se pinta, salvo `full`
 * (2160 px), que es la que permite el zoom 2× en PhotoSwipe.
 *
 * f_webp explícito (no f_auto): la URL es la clave de caché y el formato no debe depender del
 * navegador que la pide. WebP decodifica en cualquier WebView ≥ 32; AVIF queda fuera hasta
 * que la Fase 0 confirme decodificación rápida en el RK3568.
 */
export const VARIANTS = {
  thumb: { t: 'c_fill,g_auto,w_300,h_300,q_auto:good,f_webp', fit: 'fill', w: 300, h: 300 },
  // Sin recorte: la imagen completa a todo el ancho (1080) y alto proporcional, como en "Ampliar"
  main: { t: 'c_limit,w_1080,h_1000,q_auto:good,f_webp', fit: 'limit', w: 1080, h: 1000 },
  full: { t: 'c_limit,w_2160,h_2160,q_auto:good,f_webp', fit: 'limit', w: 2160, h: 2160 },
  card: { t: 'c_fill,g_auto,w_460,h_420,q_auto:good,f_webp', fit: 'fill', w: 460, h: 420 },
  attract: { t: 'c_fill,g_auto,w_1080,h_1920,q_auto:good,f_webp', fit: 'fill', w: 1080, h: 1920 },
  lqip: { t: 'c_limit,w_24,h_24,e_blur:200,q_30,f_webp', fit: 'limit', w: 24, h: 24 },
  logo: { t: 'c_limit,w_480,h_240,q_auto:best,f_webp', fit: 'limit', w: 480, h: 240 },
} as const;

export type VariantName = keyof typeof VARIANTS;

const CLOUD_RE = /res\.cloudinary\.com\/([^/]+)\//;

function cloudOf(a: CloudinaryAsset): string {
  const m = (a.secure_url || a.url || '').match(CLOUD_RE);
  return (m && m[1]) || CONFIG.cloudinaryCloud;
}

/**
 * Cambia la transformación de una URL ya construida (p. ej. la portada de 460 px de una tarjeta
 * a 1600 px cuando la tarjeta se muestra grande). En el contenido demo devuelve la URL original.
 */
export function withTransform(url: string, from: VariantName, t: string): string {
  const cur = `/${VARIANTS[from].t}/`;
  return url.includes(cur) ? url.replace(cur, `/${t}/`) : url;
}

export function variantUrl(a: CloudinaryAsset, v: VariantName): string {
  if (CONFIG.source === 'demo') {
    return `/demo/img/${a.public_id}__${v}.webp`;
  }
  const cloud = cloudOf(a);
  if (!cloud) throw new Error(`No se puede deducir el cloud de Cloudinary para ${a.public_id}`);
  const id = a.public_id.split('/').map(encodeURIComponent).join('/');
  const ver = a.version ? `v${a.version}/` : '';
  return `https://res.cloudinary.com/${cloud}/image/upload/${VARIANTS[v].t}/${ver}${id}.webp`;
}

/** Dimensiones resultantes de una variante `limit` (no amplía) */
export function limitSize(a: CloudinaryAsset, v: VariantName): { w: number; h: number } {
  const spec = VARIANTS[v];
  const w0 = a.width || spec.w;
  const h0 = a.height || spec.h;
  const k = Math.min(1, spec.w / w0, spec.h / h0);
  return { w: Math.round(w0 * k), h: Math.round(h0 * k) };
}

/** Hash FNV-1a de 64 bits (dos mitades de 32) en base36: clave estable de contenido */
export function hashKey(s: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0xcbf29ce4;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193);
    h2 = Math.imul(h2 ^ c, 0x01000193 + 0x200);
  }
  return (h1 >>> 0).toString(36) + (h2 >>> 0).toString(36);
}
